/**
 * Class description
 *
 * Created: 2017-11-01
 * @author jonas
 */
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {IReelStartSpinData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {ISymbolStateConfig} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SpinParsedGameData} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinParsedServerData";
import {SpinReelController} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinReelController";
import {TweenMax} from "gsap"
import {DungeonQuestGameMode} from "../DungeonQuestGameConfig";
import {DungeonQuestBaseReelSymbol} from "../symbols/DungeonQuestBaseReelSymbol";
import {DungeonQuestReelView} from "./DungeonQuestReelView";

interface DungeonQuestOnSpinCallback {
    owner:any;
    callback:(delta:number) => void;
}

export class DungeonQuestReelController extends SpinReelController {
    public static ADD_SPIN_START_DELAY:string = "DungeonQuestReelController_addSpinStartDelay";

    private _extraSpinStartDelay:number = 0;
    private _callbacks:DungeonQuestOnSpinCallback[];
    private _spinDelta:number;
    private _updateLoop:TweenMax | null;

    constructor(reelId:number) {
        super(reelId);
        this._callbacks = [];
        this._spinDelta = 0;
    }

    protected addEventListeners():void {
        super.addEventListeners();
        EventHandler.addEventListener(this, DungeonQuestReelController.ADD_SPIN_START_DELAY, (event:GameEvent) => this.addSpinStartDelay(event.params[0], event.params[1]));
    }

    private addSpinStartDelay(reelId:number, delay:number):void {
        if(this.reelId != reelId) {
            return;
        }
        this._extraSpinStartDelay = delay;
    }

    public onGameDataParsed(data:SpinParsedGameData):void {
        super.onGameDataParsed(data);

        if(this._gameMode == DungeonQuestGameMode.RANDOM_TRANSFORM || this._gameMode == DungeonQuestGameMode.FREESPIN) {
            this._nearWinReels = [true, true, true, true, true];
        }

        if(this._gameMode == DungeonQuestGameMode.RANDOM_WILD_TRANSFORM) {
            this._nearWinReels = [false, false, false, false, false];
        }
    }

    protected addNewSymbol(symName:string, state?:ISymbolStateConfig):DungeonQuestBaseReelSymbol {
        const symbol:DungeonQuestBaseReelSymbol = <DungeonQuestBaseReelSymbol>super.addNewSymbol(symName, state);
        return symbol;
    }

    protected startSpinAnimation(data:IReelStartSpinData, previousDelay:number):number {
        previousDelay += this._extraSpinStartDelay;
        this._extraSpinStartDelay = 0;
        return super.startSpinAnimation(data, previousDelay);
    }

    protected onSpinStartedCompleted():void {
        super.onSpinStartedCompleted();
        (<DungeonQuestReelView>this.view).startSpeedLines();
    }


    protected onSpinStopStarted():void {
        super.onSpinStopStarted();
        (<DungeonQuestReelView>this.view).stopSpeedLines();
    }

    private startLoop() {
        if(this._updateLoop != null) {
            console.warn("Loop was not properly reset");
        }
        this._updateLoop = new TweenMax(this, 1, {repeat : -1, onUpdate : () => this.onUpdate()});
    }

    private stopLoop() {
        if(this._updateLoop && this._updateLoop.isActive()) {
            this._updateLoop.pause();
            this._updateLoop.kill();
            this._updateLoop = null;
        }
        this._spinDelta = 0;
    }

    public onUpdate() {
        this._spinDelta = this._spinReelPartAnimator.yPos - this._spinReelPartAnimator.preYPos;
        for(let callback of this._callbacks) {
            callback.callback(this._spinDelta);
        }
    }

    public startSpin(data:IReelStartSpinData, previousDelay:number):number {
        const delay:number = super.startSpin(data, previousDelay);
        if(this._callbacks.length > 0) {
            this.startLoop();
        }

        return delay;
    }

    protected completeSpin():void {
        if(this._callbacks.length > 0) {
            this.stopLoop();
        }
        super.completeSpin();
    }

    public hasRegisteredCallback(callback:DungeonQuestOnSpinCallback) {
        for(let i:number = this._callbacks.length - 1; i >= 0; i--) {
            let registeredCallback = this._callbacks[i];
            if(callback.owner === registeredCallback.owner) {
                return true;
            }
        }
        return false;
    }

    public addOnSpinCallback(callback:DungeonQuestOnSpinCallback) {
        if(this.hasRegisteredCallback(callback)) {
            console.warn("Already has registered callback.");
            return;
        }
        this._callbacks.push(callback);
    }

    public removeOnSpinCallback(callback:DungeonQuestOnSpinCallback) {
        for(let i:number = this._callbacks.length - 1; i >= 0; i--) {
            let registeredCallback = this._callbacks[i];
            if(callback.owner === registeredCallback.owner) {
                this._callbacks.splice(i, 1);
            }
        }
    }

    protected getNearWinStopDelay(reelId:number):number {
        if(this.isScatterNearWin()) {
            return super.getNearWinStopDelay(reelId) * 2
        }

        if(this._reelPartAnimator.isFastSpin) {
            return super.getNearWinStopDelay(reelId) * 0.7;
        }

        return super.getNearWinStopDelay(reelId);
    }

    private isScatterNearWin() {
        return !(this._gameMode == DungeonQuestGameMode.RANDOM_TRANSFORM || this._gameMode == DungeonQuestGameMode.FREESPIN || this._gameMode == DungeonQuestGameMode.RANDOM_WILD_TRANSFORM);

    }
}